import './augmentations';

import {
  createTheme,
  type PaletteColor,
  type SimplePaletteColorOptions,
  type ThemeOptions,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { frFR } from '@mui/material/locale';
import { aiColor, darkAiColor } from './tokens/aiColors';
import { shape } from './tokens/shape';
import { spacing } from './tokens/spacing';
import { darkStateColors, type StateColorName, stateColors } from './tokens/stateColors';
import { RESOURCE_COLOR_NAMES, type ResourceColorName, resourcesColors } from './tokens/resourcesColors';
import { resourcesIcons } from './tokens/resourcesIcons';
import { typography } from './tokens/typography';

const AI_COLOR_NAME = 'ai';

const { augmentColor } = createTheme().palette;

const themeOptions: ThemeOptions = {
  shape,
  spacing,
  typography,
  resourceIcons: resourcesIcons,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          noItem: 'p',
          body1Emphasis: 'p',
          body2Emphasis: 'p',
        },
      },
      styleOverrides: {
        body2: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        body2Emphasis: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        subtitle2: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        noItem: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        icon: {
          color: 'inherit',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // Amounts and dates line up from one row to the next.
        root: {
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
  },
};

function augmentStateColors(colors: Record<StateColorName, SimplePaletteColorOptions>): Record<StateColorName, PaletteColor> {
  return {
    pending: augmentColor({ color: colors.pending, name: 'pending' }),
    done: augmentColor({ color: colors.done, name: 'done' }),
    cancelled: augmentColor({ color: colors.cancelled, name: 'cancelled' }),
    default: augmentColor({ color: colors.default, name: 'default' }),
  };
}

function augmentResourceColors(colors: typeof resourcesColors): Record<ResourceColorName, PaletteColor> {
  const result = {} as Record<ResourceColorName, PaletteColor>;
  for (const name of RESOURCE_COLOR_NAMES) {
    result[name] = augmentColor({ color: colors[name], name });
  }
  return result;
}

const aiPaletteColor = augmentColor({ color: aiColor, name: AI_COLOR_NAME });
const darkAiPaletteColor = augmentColor({ color: darkAiColor, name: AI_COLOR_NAME });
const augmentedResourceColors = augmentResourceColors(resourcesColors);

export const lightTheme = createTheme({
  ...themeOptions,
  palette: {
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
      elevation1: grey[100],
    },
    ai: aiPaletteColor,
    ...augmentStateColors(stateColors),
    ...augmentedResourceColors,
    resourcesColors: augmentedResourceColors,
  },
}, frFR);

export const darkTheme = createTheme({
  ...themeOptions,
  palette: {
    mode: 'dark',
    background: {
      elevation1: grey[900],
    },
    ai: darkAiPaletteColor,
    ia: darkAiPaletteColor,
    ...augmentStateColors(darkStateColors),
    ...augmentedResourceColors,
    resourcesColors: augmentedResourceColors,
  },
}, frFR);
